import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Bug, Image as ImageIcon, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, API, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModule, MODULE_LABELS } from "@/context/ModuleContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ISSUE_TYPES = ["Bug", "Change request", "Question"];
export const ISSUE_STATUS_STYLE = {
  Open: "bg-red-500/15 text-red-700 dark:text-red-400",
  "In progress": "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  Resolved: "bg-green-500/15 text-green-700 dark:text-green-400",
};
export const TYPE_STYLE = { Bug: "bg-rose-500/10 text-rose-700 dark:text-rose-300", "Change request": "bg-sky-500/10 text-sky-700 dark:text-sky-300", Question: "bg-sky-500/10 text-sky-700 dark:text-sky-300" };
export const IssueStatusBadge = ({ status, testid }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${ISSUE_STATUS_STYLE[status] || "bg-muted text-muted-foreground"}`} data-testid={testid}>{status}</span>
);
export const IssueTypeBadge = ({ type }) => <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${TYPE_STYLE[type] || "bg-muted"}`}>{type}</span>;
export const shotUrl = (id) => `${API}/issues/screenshot/${id}`;

const MAX_SHOTS = 3;

// Header button (observers only): raise an issue about the page you are on; module + page are captured automatically.
export default function ReportIssueButton({ pageLabel }) {
  const { user } = useAuth();
  const { module } = useModule();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [type, setType] = useState("Bug");
  const [shots, setShots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setTitle(""); setDetails(""); setType("Bug"); setShots([]); } }, [open]);
  if (!user || user.role !== "observer") return null;

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/")).slice(0, MAX_SHOTS - shots.length);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f, f.name || "screenshot.png");
        const { data } = await api.post("/issues/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setShots((s) => [...s, { ...data, preview: URL.createObjectURL(f) }]);
      }
    } catch (e) { toast.error(errorMessage(e)); } finally { setUploading(false); }
  };
  const onPaste = (e) => { const items = Array.from(e.clipboardData?.items || []).filter((i) => i.type.startsWith("image/")).map((i) => i.getAsFile()).filter(Boolean); if (items.length) { e.preventDefault(); addFiles(items); } };
  const submit = async () => {
    if (title.trim().length < 3) { toast.error("Give the issue a short title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/issues", { title: title.trim(), details: details.trim(), type, module: module || null, page_path: location.pathname + (location.search || ""), page_label: pageLabel || null, screenshot_ids: shots.map((s) => s.id) });
      toast.success(`${data.number} raised — the admin has been notified. Track it under My Issues.`);
      setOpen(false);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const where = [module ? MODULE_LABELS[module] || module : null, pageLabel].filter(Boolean).join(" · ") || location.pathname;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="report-issue-btn" title="Report an issue on this page" className="h-8 w-8 flex items-center justify-center rounded-md text-foreground hover:bg-muted transition-colors">
        <Bug size={18} weight="bold" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="report-issue-dialog" onPaste={onPaste}>
          <DialogHeader><DialogTitle className="font-display text-xl">Report an issue</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground" data-testid="report-issue-where"><span className="uppercase tracking-wider">Where</span><span className="px-2 py-0.5 rounded-md bg-muted text-foreground">{where}</span></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What is wrong or needed?" maxLength={160} className="mt-1 h-8 rounded-md" data-testid="report-issue-title" autoFocus /></div>
              <div><Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Type</Label>
                <Select value={type} onValueChange={setType}><SelectTrigger className="mt-1 h-8 rounded-md" data-testid="report-issue-type"><SelectValue /></SelectTrigger><SelectContent>{ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Details</Label><Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={4000} placeholder="Steps, what you expected, what happened. You can paste a screenshot here with Ctrl+V." className="mt-1 rounded-md" data-testid="report-issue-details" /></div>
            <div>
              <div className="flex items-center justify-between"><Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Screenshots · optional ({shots.length}/{MAX_SHOTS})</Label>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="report-issue-file" />
                <Button type="button" variant="outline" size="sm" className="h-7 rounded-md text-[11px]" disabled={uploading || shots.length >= MAX_SHOTS} onClick={() => fileRef.current?.click()} data-testid="report-issue-add-shot"><ImageIcon size={13} className="mr-1" />{uploading ? "Uploading…" : "Add screenshot"}</Button></div>
              {shots.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap" data-testid="report-issue-shots">
                  {shots.map((s) => (
                    <div key={s.id} className="relative h-16 w-24 border border-border rounded-md overflow-hidden bg-muted">
                      <img src={s.preview || shotUrl(s.id)} alt={s.filename} className="h-full w-full object-cover" />
                      <button type="button" onClick={() => setShots((x) => x.filter((y) => y.id !== s.id))} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-background/90 flex items-center justify-center hover:bg-destructive hover:text-white" data-testid={`report-issue-remove-${s.id}`}><X size={11} weight="bold" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)} data-testid="report-issue-cancel">Cancel</Button>
            <Button className="rounded-md" onClick={submit} disabled={busy || uploading || title.trim().length < 3} data-testid="report-issue-submit"><PaperPlaneTilt size={15} weight="bold" className="mr-1.5" />{busy ? "Sending…" : "Raise issue"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
